import moment from 'moment-timezone'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import CafeteriaMenu from 'app/common/components/Cafeteria/CafeteriaMenu'

import {
  cafeteriaMenuPathMask,
  cafeteriaOrdersPathMask,
  params as p,
  createPath,
  getSchoolTypeExact,
  getStudentIdExact,
  getDateExact,
  getCategoryId,
  dateFormat as routerDateFormat,
  monthFormat as routerMonthFormat,
} from 'app/pages/routePaths'

import selectPerson, { type Person } from '../selectPerson'
import useSchoolAsCamp from '../useSchoolAsCamp'
import SimpleDate from 'app/dataProviders/SimpleDate'

const dateFormatPresentative = 'DD.MM.YYYY' as const

type Props = {
  params: {
    id: string
    date: string
    schoolType: string
    categoryId?: string
  }
  router: {
    replace: (pathname: string) => void
    push: (pathname: string) => void
  }
  route: unknown
}

const MenuPage = ({ params, router: { push }, route }: Props) => {
  const { t } = useTranslation('cafeteria')

  const date = getDateExact(params)
  const id = getStudentIdExact(params)
  const schoolType = getSchoolTypeExact(params)
  const categoryId = getCategoryId(params)

  const person: Person = useSelector((state) => selectPerson(state, id))
  const school = useSchoolAsCamp(id, schoolType)

  const handleChangeDate = useCallback(
    (newDate: string, newDateFormat: string) => {
      push(
        createPath(
          {
            [p.studentId]: id,
            [p.schoolType]: schoolType,
            [p.date]: moment(newDate, newDateFormat).format(routerDateFormat),
            [p.categoryId]: categoryId,
          },
          cafeteriaMenuPathMask,
        ),
      )
    },
    [id, push, schoolType, categoryId],
  )

  const handleChangeCategory = useCallback(
    (newCategoryId: string) => {
      push(
        createPath(
          {
            [p.studentId]: id,
            [p.schoolType]: schoolType,
            [p.date]: date,
            [p.categoryId]: newCategoryId,
          },
          cafeteriaMenuPathMask,
        ),
      )
    },
    [id, push, schoolType, date],
  )

  const createUrl = useCallback(
    ({ date: newDate, categoryId: newCategoryId }: { date?: SimpleDate; categoryId?: string }) => {
      return createPath(
        {
          [p.studentId]: id,
          [p.schoolType]: schoolType,
          [p.date]:
            newDate && newDate.isValid() ? newDate.getStringFormatted(routerDateFormat) : date,
          [p.categoryId]: newCategoryId || categoryId,
        },
        cafeteriaMenuPathMask,
      )
    },
    [id, schoolType, categoryId, date],
  )

  const handleRedirectToOrders = useCallback(() => {
    push(
      createPath(
        {
          [p.studentId]: id,
          [p.schoolType]: schoolType,
          [p.date]: moment(date, routerDateFormat).format(routerMonthFormat),
        },
        cafeteriaOrdersPathMask,
      ),
    )
  }, [id, push, schoolType, date])

  const simpleDate = useMemo(() => new SimpleDate(date, routerDateFormat), [date])

  const datePresentative = simpleDate.getStringFormatted(dateFormatPresentative)
  const header = t('menu_header', { schoolTitle: school.title, datePresentative }) // Меню столовой ${schoolTitle} на ${datePresentative}

  return (
    <CafeteriaMenu
      date={simpleDate}
      categoryId={categoryId}
      header={header}
      school={school}
      person={person}
      orderDisabled={schoolType === 'camp'}
      route={route}
      onChangeDate={handleChangeDate}
      onChangeCategory={handleChangeCategory}
      createUrl={createUrl}
      onOrderSuccess={handleRedirectToOrders}
    />
  )
}

export default MenuPage
